import {Component, DestroyRef, OnInit, ViewChild, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {ConfirmationService, MessageService} from 'primeng/api';
import {Popover, PopoverModule} from 'primeng/popover';
import {BadgeModule} from 'primeng/badge';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ToastModule} from 'primeng/toast';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LocaleService} from '../../core/i18n/locale.service';
import {AdminNotificationsService} from '../../core/service/admin-notifications.service';
import {
  ADMIN_NOTIFICATION_TYPE,
  AI_ALERT_SEVERITY,
  AI_ALERT_TYPES,
  AdminNotificationDto,
  WATHQ_ALERT_TYPES,
  localizedText,
} from '../../core/modules/notifications/admin-notification.model';

const NOTIFICATION_ICONS: Record<string, string> = {
  [ADMIN_NOTIFICATION_TYPE.CertificateSubmitted]: 'pi pi-file-check',
  [ADMIN_NOTIFICATION_TYPE.ReturnEscalatedAdminAlert]: 'pi pi-exclamation-triangle',
  [ADMIN_NOTIFICATION_TYPE.AdRejectedAdminAlert]: 'pi pi-megaphone',
  [ADMIN_NOTIFICATION_TYPE.AiServiceOutageStarted]: 'pi pi-exclamation-circle',
  [ADMIN_NOTIFICATION_TYPE.AiServiceOutagePersisting]: 'pi pi-clock',
  [ADMIN_NOTIFICATION_TYPE.AiServiceOutageResolved]: 'pi pi-check-circle',
  [ADMIN_NOTIFICATION_TYPE.AiMonthlySpendWarning]: 'pi pi-wallet',
  [ADMIN_NOTIFICATION_TYPE.AiMonthlySpendLimitReached]: 'pi pi-wallet',
  [ADMIN_NOTIFICATION_TYPE.AiDailySpendLimitReached]: 'pi pi-wallet',
  [ADMIN_NOTIFICATION_TYPE.WathqServiceOutageStarted]: 'pi pi-exclamation-circle',
  [ADMIN_NOTIFICATION_TYPE.WathqServiceOutagePersisting]: 'pi pi-clock',
  [ADMIN_NOTIFICATION_TYPE.WathqServiceOutageResolved]: 'pi pi-check-circle',
  [ADMIN_NOTIFICATION_TYPE.WathqMonthlyBudgetWarning]: 'pi pi-wallet',
  [ADMIN_NOTIFICATION_TYPE.WathqMonthlyBudgetReached]: 'pi pi-wallet',
  [ADMIN_NOTIFICATION_TYPE.PickupOrderNotCollected]: 'pi pi-box',
};

const NOTIFICATION_LABEL_KEYS: Record<string, string> = {
  [ADMIN_NOTIFICATION_TYPE.CertificateSubmitted]: 'notifications.typeCertificateSubmitted',
  [ADMIN_NOTIFICATION_TYPE.ReturnEscalatedAdminAlert]: 'notifications.typeReturnEscalated',
  [ADMIN_NOTIFICATION_TYPE.AdRejectedAdminAlert]: 'notifications.typeAdRejected',
};

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [
    CommonModule,
    PopoverModule,
    BadgeModule,
    ConfirmDialogModule,
    ToastModule,
    TranslatePipe,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.css',
})
export class NotificationBellComponent implements OnInit {
  @ViewChild('op') popover!: Popover;

  private readonly notificationsService = inject(AdminNotificationsService);
  private readonly confirmService = inject(ConfirmationService);
  private readonly toast = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  unreadCount = 0;
  notifications: AdminNotificationDto[] = [];
  loading = false;
  loadingMore = false;
  page = 1;
  pageSize = 10;
  totalPages = 1;

  ngOnInit(): void {
    this.notificationsService.pollUnreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => this.unreadCount = status.unreadCount);
  }

  get badgeValue(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  get hasMore(): boolean {
    return this.page < this.totalPages;
  }

  loadFirstPage(): void {
    this.loading = true;
    this.page = 1;
    this.notificationsService.getNotifications(1, this.pageSize).subscribe({
      next: res => {
        this.loading = false;
        if (res.isSuccess && res.data) {
          this.notifications = res.data.items;
          this.page = res.data.pageNumber;
          this.totalPages = res.data.totalPages;
        }
      },
      error: () => this.loading = false,
    });
  }

  loadMore(): void {
    if (!this.hasMore || this.loadingMore) return;
    this.loadingMore = true;
    const nextPage = this.page + 1;
    this.notificationsService.getNotifications(nextPage, this.pageSize).subscribe({
      next: res => {
        this.loadingMore = false;
        if (res.isSuccess && res.data) {
          this.notifications = [...this.notifications, ...res.data.items];
          this.page = res.data.pageNumber;
          this.totalPages = res.data.totalPages;
        }
      },
      error: () => this.loadingMore = false,
    });
  }

  onItemClick(notification: AdminNotificationDto): void {
    if (!notification.isRead) {
      this.markRead(notification);
    }
    const link = this.notificationsService.deepLinkFor(notification);
    this.popover.hide();
    if (link) {
      this.router.navigateByUrl(link);
    }
  }

  markAllRead(): void {
    this.confirmService.confirm({
      message: this.translate.instant('notifications.confirmMarkAllReadMessage'),
      header: this.translate.instant('notifications.confirmMarkAllReadHeader'),
      icon: 'pi pi-check-circle',
      accept: () => {
        this.notificationsService.markAllAsRead().subscribe({
          next: res => {
            if (res.isSuccess) {
              this.notifications = this.notifications.map(n => ({...n, isRead: true}));
              this.unreadCount = 0;
              this.toast.add({
                severity: 'success',
                summary: this.translate.instant('notifications.successSummary'),
                detail: this.translate.instant('notifications.markedAllReadSuccess'),
              });
            }
          },
          error: () => {},
        });
      },
    });
  }

  clearAll(): void {
    this.confirmService.confirm({
      message: this.translate.instant('notifications.confirmClearAllMessage'),
      header: this.translate.instant('notifications.confirmClearAllHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.notificationsService.clearAll().subscribe({
          next: res => {
            if (res.isSuccess) {
              this.notifications = [];
              this.unreadCount = 0;
              this.page = 1;
              this.totalPages = 1;
              this.toast.add({
                severity: 'success',
                summary: this.translate.instant('notifications.successSummary'),
                detail: this.translate.instant('notifications.clearedSuccess'),
              });
            }
          },
          error: () => {},
        });
      },
    });
  }

  iconFor(notification: AdminNotificationDto): string {
    const icon = NOTIFICATION_ICONS[notification.type] ?? 'pi pi-bell';
    const severity = AI_ALERT_SEVERITY[notification.type];
    return severity ? `${icon} severity-${severity}` : icon;
  }

  labelFor(notification: AdminNotificationDto): string {
    const key = NOTIFICATION_LABEL_KEYS[notification.type];
    if (key) {
      return this.translate.instant(key);
    }
    return localizedText(notification.title, this.localeService.current) ?? '';
  }

  // AI and Wathq alerts carry their facts (spend vs limit, the outage's cause, since when)
  // only in the backend's localised body, so show it under the title. So does the uncollected
  // pickup alert (which order, which supplier and buyer, how many days).
  bodyFor(notification: AdminNotificationDto): string | null {
    if (!AI_ALERT_TYPES.has(notification.type)
      && !WATHQ_ALERT_TYPES.has(notification.type)
      && notification.type !== ADMIN_NOTIFICATION_TYPE.PickupOrderNotCollected) return null;
    return localizedText(notification.body, this.localeService.current);
  }

  trackById(_index: number, notification: AdminNotificationDto): string {
    return notification.id;
  }

  private markRead(notification: AdminNotificationDto): void {
    notification.isRead = true;
    this.unreadCount = Math.max(0, this.unreadCount - 1);
    this.notificationsService.markAsRead([notification.id]).subscribe({error: () => {}});
  }
}
